/* eslint no-underscore-dangle: ["error", { "allow": ["__INITIAL_STATE__", "__REDUX_STORE__"] }] */
import { applyMiddleware, legacy_createStore as createStore } from 'redux';
import { composeWithDevTools } from 'redux-devtools-extension/developmentOnly';
import thunk from 'redux-thunk';
import omit from 'lodash/omit';

import { getUrlParam } from 'Utils/Content';
import { configureAuthStore, isNonAnonymousUser, isAnonymousUser, getUserMvpdId, isMvpdUser } from 'Utils/UserCookie';
import { initialUserState } from 'State/User/UserReducers';
import { logger } from 'Utils/Logger';
import reducers from './reducers';

const isSSR = getUrlParam('isSSR', true);

const getDefaultState = (state) => {
  let defaultState = {};
  if (state) {
    const { plans, pageLayout, ...rest } = state;
    if (pageLayout && pageLayout.footerContent) {
      pageLayout.footerContent.isLoaded = true;
    }
    // to always refetch plans on client
    if (plans) {
      plans.getPlansSuccess = false;
      plans.isFetchingPlans = false;
    }

    defaultState = { ...rest, plans, pageLayout };
  }
  return defaultState;
};

const getAppConfig = () => {
  const screens = GLOBAL_SCREENS;
  const routes = GLOBAL_ROUTES;
  const strings = GLOBAL_STRINGS;
  const defaultConfig = GLOBAL_CONFIG;
  const pageMeta = GLOBAL_PAGEMETA;

  return { screens, strings, routes, defaultConfig, pageMeta };
};

const getInitialUserState = (config) => {
  try {
    // init auth store first time
    const token = configureAuthStore(config);
    const isAnonymous = isAnonymousUser();
    const isAuth = token && isNonAnonymousUser();
    if (isAuth) {
      // identity account type
      // remove accessToken and tokenExp before initial redux state.
      const user = omit(token, ['accessToken', 'tokenExpiration', 'accessTokenExpiration']);
      logger.setAttr('profile_id', user.profileId);
      return {
        ...initialUserState,
        isAuthenticated: true,
        ...user,
      };
    }
    if (isAnonymous) {
      const user = omit(token, ['accessToken', 'tokenExpiration', 'accessTokenExpiration']);
      const { preauthorizedResources = [] } = user;
      const hasAuthorizedResources = preauthorizedResources.includes(config.defaultConfig.entitlementId);
      const hasMvpd = hasAuthorizedResources && isMvpdUser();
      const mvpdId = (hasMvpd && getUserMvpdId()) || '';
      return {
        ...initialUserState,
        isAnonymousUser: true,
        hasMvpd,
        mvpdId,
        ...user,
      };
    }
  } catch (e) {
    console.log(e);
  }
  return undefined;
};

const getInitialState = (state = {}) => {
  const defaultState = getDefaultState(state);
  const appConfig = getAppConfig();
  const user = getInitialUserState(appConfig);
  return { ...defaultState, appConfig, user };
};

// to be invoked once per app lifecycle
const configureStore = () => {
  const initialState = getInitialState(window.__INITIAL_STATE__);
  return createStore(reducers, initialState, composeWithDevTools(applyMiddleware(thunk)));
};

const store = configureStore();

if (isSSR) {
  // required for server side rendering
  window.__REDUX_STORE__ = store;
}

export default store;
